import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);
const THEME_KEY = "epil_theme";

export const EPIL_ROLES = ["Admin", "Approver", "GM", "Reporting", "Finance", "Senior stakeholder"];
export const EDITOR_ROLES = ["Admin", "GM", "Reporting", "Finance"];

function applyTheme(theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  localStorage.setItem(THEME_KEY, theme);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [theme, setThemeState] = useState(localStorage.getItem(THEME_KEY) || "light");

  useEffect(() => applyTheme(theme), [theme]);

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.theme) setThemeState(data.theme);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
    const onUnauth = () => setUser(false);
    window.addEventListener("epil:unauthorised", onUnauth);
    return () => window.removeEventListener("epil:unauthorised", onUnauth);
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    if (data.user.theme) setThemeState(data.user.theme);
    return data.user;
  };

  const setSession = (token, u) => { localStorage.setItem(TOKEN_KEY, token); setUser(u); };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const toggleTheme = async () => {
    const next = theme === "dark" ? "light" : "dark";
    setThemeState(next);
    if (user) {
      try { await api.patch("/auth/me", { theme: next }); } catch { /* keep local */ }
    }
  };

  const roles = user?.roles || [];
  const isPartner = roles.includes("Partner");
  const value = {
    user, login, logout, theme, toggleTheme, reload: loadMe, setSession,
    isPartner, partnerActive: !isPartner || (user?.partner_status || "Active") === "Active",
    isAdmin: !isPartner && roles.includes("Admin"),
    isEpil: !isPartner && roles.some((r) => EPIL_ROLES.includes(r)),
    canEdit: !isPartner && roles.some((r) => EDITOR_ROLES.includes(r)),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
